import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import {
  callAdminCreateTenant,
  callCheckTenantSlugAvailability,
  callGetAgentByCode,
  type AdminCreateTenantOutput,
  type GetAgentByCodeOutput,
} from '../lib/callables/tenants.js';
import { celebrateOnce, ghostButtonStyle, haptic, inputStyle, primaryButtonStyle } from '@kilat/ui';
import { logger } from '../lib/logger.js';
import { PAGE_CONTENT_MAX } from '../lib/layout.js';
import {
  phone as phoneUtil,
  type TenantProfile,
  customerAppUrl,
  customerAppUrlForDisplay,
} from '@kilat/shared';

/**
 * Local row type for the recent-tenants list. `tenantsRepo` returns
 * `{ profile }` shapes with a synthesised `id`.
 */
interface TenantRow {
  readonly id?: string;
  readonly profile: TenantProfile;
}

/**
 * /superadmin/tenants — Sprint 118.
 *
 * Super-admin manual operator onboarding for white-glove signups,
 * pilots, demos, and recovered-stuck-signup cases.
 *
 * Wizard shape:
 *   1. Business basics — name, owner name, phone (E.164 normalised),
 *      optional email, locale
 *   2. Acquisition — admin / agent-referred (with live code lookup that
 *      mirrors the marketing /signup UX)
 *   3. Review — derived slug + customer-pwa URL preview with live
 *      availability check
 *   4. Success — copy slug + customer URL, dopamine moment, link to
 *      next-step (or "create another")
 *
 * Below the wizard: recent tenants list (last 20 by createdAt) so the
 * super-admin sees what they've built recently without leaving the page.
 *
 * Per [feedback-no-dead-ends] every state explicit. Per [feedback-flows-
 * premium-logical] every step has loading / valid / invalid + the next
 * action is always visible. Per [feedback-dopamine-for-all] success
 * fires celebrateOnce + haptic.
 */
export function SuperadminTenantsPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const isSuperAdmin = isSuperAdminForBrand(claims, brand.id);

  if (authLoading) {
    return <Screen title="Signing you in…" body="One second." />;
  }
  if (!user) {
    return (
      <Screen
        title="You're signed out."
        body={`Sign in with a super-admin account for ${brand.name} to onboard operators here.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }
  if (!isSuperAdmin) {
    return (
      <Screen
        title="You're not a super-admin here."
        body={`Your account isn't a super-admin for ${brand.name}. Ask the platform owner if you should be.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--color-surface)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <div
        style={{
          maxWidth: PAGE_CONTENT_MAX,
          margin: '0 auto',
          display: 'grid',
          gap: 'var(--space-6)',
        }}
      >
        <Header />
        {/* The wizard and the operators it produced side by side on a wide
            screen; stacked (wizard first) on a phone. */}
        <div className="layout-split-aside">
          <Wizard
            brandId={brand.id}
            brandName={brand.name}
            marketingDomain={brand.domains.marketing}
          />
          <RecentTenants brandId={brand.id} />
        </div>
      </div>
    </main>
  );
}

// ── header ────────────────────────────────────────────────────────────

function Header() {
  return (
    <header>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        Super-admin
      </p>
      <h1 style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}>
        Operators
      </h1>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
          maxWidth: 580,
        }}
      >
        Manually onboard an operator when self-signup isn't the right fit — pilots, partner deals,
        recovered signups. Every step here matches the public signup write path, so the operator's
        tenant looks identical to one that came in through <code>kilat.com/signup</code>.
      </p>
    </header>
  );
}

// ── wizard ────────────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3 | 4;

interface SuccessState {
  readonly result: AdminCreateTenantOutput;
  readonly customerUrl: string;
  readonly businessName: string;
}

function Wizard({
  brandId,
  brandName,
  marketingDomain,
}: {
  readonly brandId: string;
  readonly brandName: string;
  /** This brand's own domain, from the live brand — never guessed. */
  readonly marketingDomain: string;
}) {
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 fields
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [email, setEmail] = useState('');
  const [locale, setLocale] = useState<'en' | 'ms' | 'zh' | 'ta'>('en');

  // Step 2 fields
  const [attribution, setAttribution] = useState<'admin' | 'agent'>('admin');
  const [agentCode, setAgentCode] = useState('');
  const [agentLookup, setAgentLookup] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'resolved'; agent: GetAgentByCodeOutput }
    | { kind: 'not_found' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Step 3 fields
  const [slugPreview, setSlugPreview] = useState<string>('');
  const [slugAvail, setSlugAvail] = useState<'unknown' | 'checking' | 'available' | 'taken'>(
    'unknown',
  );

  // Commit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Live phone normalisation. Mirrors the marketing /signup form: accept
  // whatever the operator types, normalise to E.164 for the API call.
  const normalisedPhone = useMemo(
    () => phoneUtil.normaliseMalaysianPhone(phoneInput),
    [phoneInput],
  );
  const phoneValid = normalisedPhone !== null;

  // Live agent-code lookup (debounced). Mirrors the marketing form's UX
  // exactly — show "Looking up…" while resolving, "Introduced by Aiman"
  // when resolved, "We couldn't find that code" on miss.
  useEffect(() => {
    if (attribution !== 'agent') {
      setAgentLookup({ kind: 'idle' });
      return;
    }
    const code = agentCode.trim().toUpperCase();
    if (code.length < 3) {
      setAgentLookup({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setAgentLookup({ kind: 'loading' });
    const t = window.setTimeout(async () => {
      try {
        const agent = await callGetAgentByCode({
          code,
          originHost: window.location.hostname,
        });
        if (cancelled) return;
        if (agent.platformBrandId !== brandId) {
          setAgentLookup({ kind: 'not_found' });
        } else {
          setAgentLookup({ kind: 'resolved', agent });
        }
      } catch (err) {
        if (cancelled) return;
        const msg = (err as { message?: string }).message ?? '';
        if (msg.includes('not-found')) {
          setAgentLookup({ kind: 'not_found' });
        } else {
          setAgentLookup({ kind: 'error', message: msg || 'Lookup failed.' });
        }
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [attribution, agentCode, brandId]);

  // Step 3 — derive a slug suggestion from the business name (light
  // kebab-case). The server is the source of truth (it'll find an
  // available variant if this one's taken), but the UI shows the most
  // likely outcome so the super-admin isn't surprised.
  useEffect(() => {
    if (step !== 3) return;
    const suggested = slugify(businessName);
    setSlugPreview(suggested);
    if (!suggested) {
      setSlugAvail('unknown');
      return;
    }
    let cancelled = false;
    setSlugAvail('checking');
    const t = window.setTimeout(async () => {
      try {
        const result = await callCheckTenantSlugAvailability({
          platformBrandId: brandId,
          slug: suggested,
        });
        if (cancelled) return;
        setSlugAvail(result.available ? 'available' : 'taken');
      } catch (err) {
        if (cancelled) return;
        logger.warn('slug availability check failed', { error: String(err) });
        setSlugAvail('unknown');
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [step, businessName, brandId]);

  const step1Valid = businessName.trim().length >= 2 && ownerName.trim().length >= 2 && phoneValid;
  const step2Valid =
    attribution === 'admin' || (attribution === 'agent' && agentLookup.kind === 'resolved');

  async function commit() {
    if (!step1Valid || !step2Valid || !normalisedPhone) return;
    setSubmitting(true);
    setError(null);
    haptic.tap();
    try {
      const result = await callAdminCreateTenant({
        platformBrandId: brandId,
        businessName: businessName.trim(),
        ownerName: ownerName.trim(),
        ownerPhone: normalisedPhone,
        ...(email.trim() ? { ownerEmail: email.trim() } : {}),
        locale,
        ...(attribution === 'agent' && agentLookup.kind === 'resolved'
          ? { agentCode: agentLookup.agent.code }
          : {}),
      });
      const customerUrl = customerAppUrl({ marketing: marketingDomain }, result.slug);
      setSuccess({ result, customerUrl, businessName: businessName.trim() });
      void celebrateOnce({
        key: `superadmin-onboarded:${brandId}`,
        intensity: 'milestone',
      }).then((fired) => {
        if (fired) haptic.celebration();
        else haptic.success();
      });
    } catch (err) {
      logger.error('adminCreateTenant failed', { error: String(err) });
      const msg = (err as { message?: string }).message ?? '';
      if (msg.includes('permission-denied')) {
        setError(
          "You don't have super-admin rights on this brand. Sign in with a super-admin account.",
        );
      } else if (msg.includes('resource-exhausted')) {
        setError(
          'That business name collided with too many existing slugs. Tweak the name slightly and try again.',
        );
      } else if (msg.includes('invalid-argument')) {
        setError('One of the fields is invalid. Go back and check the highlighted entries.');
      } else {
        setError(msg || 'Something tripped while creating the operator. Try once more.');
      }
      setSubmitting(false);
    }
  }

  function reset() {
    setStep(1);
    setBusinessName('');
    setOwnerName('');
    setPhoneInput('');
    setEmail('');
    setLocale('en');
    setAttribution('admin');
    setAgentCode('');
    setAgentLookup({ kind: 'idle' });
    setSlugPreview('');
    setSlugAvail('unknown');
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  }

  if (success) {
    return <SuccessCard state={success} brandName={brandName} onAnother={reset} />;
  }

  return (
    <section
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--border-soft, var(--color-border))',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-5)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <StepIndicator current={step} />

      {step === 1 ? (
        <Step1Basics
          businessName={businessName}
          setBusinessName={setBusinessName}
          ownerName={ownerName}
          setOwnerName={setOwnerName}
          phoneInput={phoneInput}
          setPhoneInput={setPhoneInput}
          phoneValid={phoneValid}
          normalisedPhone={normalisedPhone}
          email={email}
          setEmail={setEmail}
          locale={locale}
          setLocale={setLocale}
        />
      ) : step === 2 ? (
        <Step2Attribution
          attribution={attribution}
          setAttribution={setAttribution}
          agentCode={agentCode}
          setAgentCode={setAgentCode}
          agentLookup={agentLookup}
        />
      ) : step === 3 ? (
        <Step3Review
          businessName={businessName}
          ownerName={ownerName}
          phoneE164={normalisedPhone ?? phoneInput}
          email={email}
          locale={locale}
          attribution={attribution}
          agentLookup={agentLookup}
          slugPreview={slugPreview}
          slugAvail={slugAvail}
          marketingDomain={marketingDomain}
        />
      ) : null}

      {error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            color: '#991b1b',
            fontSize: 'var(--text-meta)',
            lineHeight: 1.5,
          }}
        >
          {error}
        </p>
      ) : null}

      <Footer
        step={step}
        canAdvance={step === 1 ? step1Valid : step === 2 ? step2Valid : true}
        submitting={submitting}
        onBack={() => setStep((step - 1) as WizardStep)}
        onNext={() => setStep((step + 1) as WizardStep)}
        onCommit={() => void commit()}
      />
    </section>
  );
}

// ── step indicator ────────────────────────────────────────────────────

function StepIndicator({ current }: { readonly current: WizardStep }) {
  const steps: { id: WizardStep; label: string }[] = [
    { id: 1, label: 'Business' },
    { id: 2, label: 'Attribution' },
    { id: 3, label: 'Review' },
  ];
  return (
    <nav
      aria-label="Wizard step"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
      }}
    >
      {steps.map((s, i) => {
        const done = current > s.id;
        const active = current === s.id;
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span
              aria-current={active ? 'step' : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: active
                  ? 'var(--color-primary)'
                  : done
                    ? 'rgba(13,122,79,0.16)'
                    : 'var(--color-border)',
                color: active
                  ? 'var(--color-primary-fg)'
                  : done
                    ? 'var(--color-primary)'
                    : 'var(--color-on-surface-muted)',
                fontWeight: 600,
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {done ? '✓' : s.id}
            </span>
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: active ? 'var(--color-on-surface)' : 'var(--color-on-surface-muted)',
                fontWeight: active ? 500 : 400,
              }}
            >
              {s.label}
            </span>
            {i < steps.length - 1 ? (
              <span
                aria-hidden
                style={{ width: 24, height: 1, background: 'var(--color-border-strong)' }}
              />
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

// ── step 1: business basics ────────────────────────────────────────────

function Step1Basics({
  businessName,
  setBusinessName,
  ownerName,
  setOwnerName,
  phoneInput,
  setPhoneInput,
  phoneValid,
  normalisedPhone,
  email,
  setEmail,
  locale,
  setLocale,
}: {
  readonly businessName: string;
  readonly setBusinessName: (v: string) => void;
  readonly ownerName: string;
  readonly setOwnerName: (v: string) => void;
  readonly phoneInput: string;
  readonly setPhoneInput: (v: string) => void;
  readonly phoneValid: boolean;
  readonly normalisedPhone: string | null;
  readonly email: string;
  readonly setEmail: (v: string) => void;
  readonly locale: 'en' | 'ms' | 'zh' | 'ta';
  readonly setLocale: (v: 'en' | 'ms' | 'zh' | 'ta') => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <SectionHeader
        title="Who's the operator?"
        body="The basics we need to provision the tenant. The owner will sign into the dashboard by phone OTP — email is for receipts."
      />

      <Field label="Business name" required>
        <input
          autoComplete="organization"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="e.g. Sparkle Carwash"
          maxLength={100}
          style={inputStyle}
        />
      </Field>

      <Field label="Owner full name" required>
        <input
          autoComplete="name"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="e.g. Aisha binti Rahman"
          maxLength={100}
          style={inputStyle}
        />
      </Field>

      <Field
        label="Owner phone"
        required
        hint={
          phoneInput.length === 0
            ? 'Malaysian numbers preferred — 012-345 6789 or +60 12 345 6789. We normalise on submit.'
            : phoneValid && normalisedPhone
              ? `✓ Will store as ${normalisedPhone}`
              : '× Not a valid MY phone yet. Format like 012-345 6789.'
        }
        hintTone={phoneInput.length === 0 ? 'muted' : phoneValid ? 'success' : 'warn'}
      >
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
          placeholder="012-345 6789"
          style={inputStyle}
        />
      </Field>

      <Field
        label="Owner email"
        hint="Optional — we'll synthesise a phone-based placeholder if you leave it blank. Real email is helpful for billing receipts."
      >
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@business.com"
          style={inputStyle}
        />
      </Field>

      <Field
        label="Default locale"
        hint="The language their dashboard + customer-pwa default to. Operators can change this any time."
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {(
            [
              { id: 'en', label: 'English' },
              { id: 'ms', label: 'Bahasa Melayu' },
              { id: 'zh', label: '中文' },
              { id: 'ta', label: 'தமிழ்' },
            ] as const
          ).map((opt) => {
            const active = locale === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                aria-pressed={active}
                onClick={() => setLocale(opt.id)}
                style={pillStyle(active)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

// ── step 2: attribution ────────────────────────────────────────────────

function Step2Attribution({
  attribution,
  setAttribution,
  agentCode,
  setAgentCode,
  agentLookup,
}: {
  readonly attribution: 'admin' | 'agent';
  readonly setAttribution: (v: 'admin' | 'agent') => void;
  readonly agentCode: string;
  readonly setAgentCode: (v: string) => void;
  readonly agentLookup:
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'resolved'; agent: GetAgentByCodeOutput }
    | { kind: 'not_found' }
    | { kind: 'error'; message: string };
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <SectionHeader
        title="How did this operator come in?"
        body="We record this on the tenant's profile so commission math + acquisition reports stay accurate. Default 'super-admin' if it's a white-glove signup with no agent attached."
      />

      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <RadioCard
          selected={attribution === 'admin'}
          onSelect={() => setAttribution('admin')}
          title="Super-admin onboarded"
          body="You're setting this up directly. No agent commission accrues."
        />
        <RadioCard
          selected={attribution === 'agent'}
          onSelect={() => setAttribution('agent')}
          title="Agent referral"
          body="An agent introduced this operator. Their one-time signup commission accrues when the operator's first invoice settles."
        />
      </div>

      {attribution === 'agent' ? (
        <Field
          label="Agent code"
          hint={
            agentLookup.kind === 'idle'
              ? 'Type the agent code exactly as printed on their card. Case-insensitive.'
              : agentLookup.kind === 'loading'
                ? 'Looking up agent…'
                : agentLookup.kind === 'resolved'
                  ? `✓ Introduced by ${agentLookup.agent.name}`
                  : agentLookup.kind === 'not_found'
                    ? '× No active agent has that code on this brand. Double-check, or switch to super-admin onboarded.'
                    : `× ${agentLookup.message}`
          }
          hintTone={
            agentLookup.kind === 'resolved'
              ? 'success'
              : agentLookup.kind === 'not_found' || agentLookup.kind === 'error'
                ? 'warn'
                : 'muted'
          }
        >
          <input
            value={agentCode}
            onChange={(e) => setAgentCode(e.target.value.toUpperCase())}
            placeholder="ABC-1234"
            autoCapitalize="characters"
            maxLength={20}
            style={inputStyle}
          />
        </Field>
      ) : null}
    </div>
  );
}

// ── step 3: review ─────────────────────────────────────────────────────

function Step3Review({
  businessName,
  ownerName,
  phoneE164,
  email,
  locale,
  attribution,
  agentLookup,
  slugPreview,
  slugAvail,
  marketingDomain,
}: {
  readonly businessName: string;
  readonly ownerName: string;
  readonly phoneE164: string;
  readonly email: string;
  readonly locale: 'en' | 'ms' | 'zh' | 'ta';
  readonly attribution: 'admin' | 'agent';
  readonly agentLookup:
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'resolved'; agent: GetAgentByCodeOutput }
    | { kind: 'not_found' }
    | { kind: 'error'; message: string };
  readonly slugPreview: string;
  readonly slugAvail: 'unknown' | 'checking' | 'available' | 'taken';
  readonly marketingDomain: string;
}) {
  const urlPreview = slugPreview
    ? customerAppUrlForDisplay({ marketing: marketingDomain }, slugPreview)
    : '—';
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <SectionHeader
        title="Ready to create?"
        body="One transaction writes the tenant, owner staff row, branding, first outlet, and seeded catalog. Nothing partial — succeeds whole or fails whole."
      />

      <ReviewBlock label="Business name" value={businessName} />
      <ReviewBlock
        label="Owner"
        value={`${ownerName} · ${phoneE164}${email ? ` · ${email}` : ''}`}
      />
      <ReviewBlock label="Default locale" value={localeLabel(locale)} />
      <ReviewBlock
        label="Attribution"
        value={
          attribution === 'admin'
            ? 'Super-admin onboarded — no agent commission'
            : agentLookup.kind === 'resolved'
              ? `Agent referral — ${agentLookup.agent.name} (${agentLookup.agent.code})`
              : 'Agent referral'
        }
      />
      <ReviewBlock
        label="Customer PWA URL"
        value={
          <span
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <code
              style={{
                fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                fontSize: 'var(--text-meta)',
                background: 'var(--tint-subtle)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {urlPreview}
            </code>
            <SlugBadge state={slugAvail} />
          </span>
        }
        hint={
          slugAvail === 'taken'
            ? "Slug's taken — the server will allocate a numeric variant (e.g. -2). Tweak the business name above if you want a cleaner one."
            : slugAvail === 'available'
              ? 'Slug is free at the moment. Final allocation happens server-side at commit.'
              : slugAvail === 'checking'
                ? 'Checking availability…'
                : null
        }
      />
    </div>
  );
}

function SlugBadge({ state }: { readonly state: 'unknown' | 'checking' | 'available' | 'taken' }) {
  const map = {
    unknown: { label: '—', bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' },
    checking: {
      label: 'Checking…',
      bg: 'var(--color-border)',
      fg: 'var(--color-on-surface-muted)',
    },
    available: { label: '✓ Available', bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)' },
    taken: { label: '× Will get -2 suffix', bg: 'rgba(245,158,11,0.10)', fg: '#92400e' },
  } as const;
  const v = map[state];
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: v.bg,
        color: v.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {v.label}
    </span>
  );
}

// ── footer ────────────────────────────────────────────────────────────

function Footer({
  step,
  canAdvance,
  submitting,
  onBack,
  onNext,
  onCommit,
}: {
  readonly step: WizardStep;
  readonly canAdvance: boolean;
  readonly submitting: boolean;
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly onCommit: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={step === 1 || submitting}
        style={{
          ...ghostButtonStyle,
          opacity: step === 1 ? 0.4 : 1,
          cursor: step === 1 ? 'not-allowed' : 'pointer',
        }}
      >
        Back
      </button>
      {step < 3 ? (
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          style={{
            ...primaryButtonStyle,
            opacity: canAdvance ? 1 : 0.5,
            cursor: canAdvance ? 'pointer' : 'not-allowed',
          }}
        >
          Next →
        </button>
      ) : (
        <button
          type="button"
          onClick={onCommit}
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'Creating operator…' : 'Create operator'}
        </button>
      )}
    </div>
  );
}

// ── success card ───────────────────────────────────────────────────────

function SuccessCard({
  state,
  brandName,
  onAnother,
}: {
  readonly state: SuccessState;
  readonly brandName: string;
  readonly onAnother: () => void;
}) {
  const [copied, setCopied] = useState<'url' | 'slug' | null>(null);

  async function copy(value: string, kind: 'url' | 'slug') {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      haptic.success();
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
    } catch {
      /* clipboard denied — fall through silently */
    }
  }

  return (
    <section
      style={{
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.22)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <header>
        <p style={metaEyebrow}>{brandName} · created just now</p>
        <h2 style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}>
          {state.businessName} is live.
        </h2>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          Tenant doc, owner staff row, branding, first outlet, and seeded catalog all written in one
          transaction. 14-day trial started today.{' '}
          {state.result.isNewOwner
            ? "We created a fresh Firebase Auth user for the owner — they'll sign in with phone OTP."
            : 'Owner already had a Firebase account — we linked them as owner. They can sign in with their existing credentials.'}
        </p>
      </header>

      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <CopyRow
          label="Customer PWA"
          value={state.customerUrl}
          mono
          copied={copied === 'url'}
          onCopy={() => void copy(state.customerUrl, 'url')}
        />
        <CopyRow
          label="Tenant slug"
          value={state.result.slug}
          mono
          copied={copied === 'slug'}
          onCopy={() => void copy(state.result.slug, 'slug')}
        />
        <CopyRow label="Owner Firebase uid" value={state.result.ownerUid} mono />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <a href={state.customerUrl} target="_blank" rel="noreferrer" style={primaryButtonStyle}>
          Open customer PWA ↗
        </a>
        <button type="button" onClick={onAnother} style={ghostButtonStyle}>
          Onboard another operator
        </button>
      </div>
    </section>
  );
}

function CopyRow({
  label,
  value,
  mono,
  copied,
  onCopy,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly copied?: boolean;
  readonly onCopy?: () => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {label}
      </span>
      <code
        style={{
          fontFamily: mono ? 'var(--font-mono, ui-monospace, monospace)' : 'inherit',
          fontSize: 'var(--text-meta)',
          background: 'var(--tint-subtle)',
          padding: '6px 10px',
          borderRadius: 'var(--radius-sm)',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </code>
      {onCopy ? (
        <button
          type="button"
          onClick={onCopy}
          style={{
            background: 'transparent',
            color: 'var(--color-primary)',
            border: 'none',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            minWidth: 80,
            textAlign: 'right',
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      ) : null}
    </div>
  );
}

// ── recent tenants ─────────────────────────────────────────────────────

function RecentTenants({ brandId }: { readonly brandId: string }) {
  const subscribe = useMemo(
    () => (cb: (vs: TenantRow[]) => void) => tenantsRepo.listSnapshot(cb),
    [],
  );
  const {
    data: tenants,
    loading,
    error,
  } = useTrackedSnapshot<TenantRow[]>(['superadmin-tenants', brandId], subscribe);

  const list = useMemo(() => {
    if (!tenants) return [];
    return tenants
      .filter((t) => t.profile.platformBrandId === brandId)
      .sort((a, b) => {
        const aMs = a.profile.createdAt.toMillis?.() ?? 0;
        const bMs = b.profile.createdAt.toMillis?.() ?? 0;
        return bMs - aMs;
      })
      .slice(0, 20);
  }, [tenants, brandId]);

  return (
    <section
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--border-soft, var(--color-border))',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>Recent operators</h2>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          Last 20 by creation date
        </span>
      </header>

      {loading ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          Loading…
        </p>
      ) : error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          Could not load tenants: {error.message}
        </p>
      ) : list.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          No operators yet on this brand. Use the new-operator wizard on this page to onboard your
          first one.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {list.map((t) => (
            <RecentTenantRow key={t.id} tenant={t} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentTenantRow({ tenant }: { readonly tenant: TenantRow }) {
  const createdAtMs = tenant.profile.createdAt.toMillis?.() ?? 0;
  const created = new Date(createdAtMs);
  const ago = formatRelative(created);
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 'var(--space-3)',
        alignItems: 'center',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 500 }}>{tenant.profile.businessName}</p>
        <p
          style={{
            margin: '2px 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {tenant.profile.slug} · {tenant.profile.acquisition.channel}
          {tenant.profile.acquisition.channel === 'agent' && tenant.profile.acquisition.agentId
            ? ` · agent ${tenant.profile.acquisition.agentId}`
            : ''}
        </p>
      </div>
      <span
        style={{
          padding: '2px 8px',
          borderRadius: 'var(--radius-pill)',
          background:
            tenant.profile.status === 'trial'
              ? 'rgba(13,122,79,0.10)'
              : tenant.profile.status === 'active'
                ? 'rgba(13,122,79,0.16)'
                : tenant.profile.status === 'past_due'
                  ? 'rgba(245,158,11,0.16)'
                  : 'var(--color-border)',
          color:
            tenant.profile.status === 'trial' || tenant.profile.status === 'active'
              ? 'var(--color-primary)'
              : tenant.profile.status === 'past_due'
                ? '#92400e'
                : 'var(--color-on-surface-muted)',
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
        }}
      >
        {tenant.profile.status}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {ago}
      </span>
    </li>
  );
}

// ── helpers ───────────────────────────────────────────────────────────

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return (sa as Record<string, unknown>)[brandId] === true;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

function localeLabel(l: 'en' | 'ms' | 'zh' | 'ta'): string {
  return l === 'en' ? 'English' : l === 'ms' ? 'Bahasa Melayu' : l === 'zh' ? '中文' : 'தமிழ்';
}

function formatRelative(d: Date): string {
  const now = Date.now();
  const diff = now - d.getTime();
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString();
}

// ── reusable primitives ───────────────────────────────────────────────

function Field({
  label,
  required,
  hint,
  hintTone,
  children,
}: {
  readonly label: string;
  readonly required?: boolean;
  readonly hint?: string | null;
  readonly hintTone?: 'muted' | 'success' | 'warn';
  readonly children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span
        style={{ fontSize: 'var(--text-meta)', fontWeight: 500, color: 'var(--color-on-surface)' }}
      >
        {label}
        {required ? <span style={{ color: 'var(--color-accent)' }}> *</span> : null}
      </span>
      {children}
      {hint ? (
        <span
          style={{
            fontSize: 'var(--text-meta)',
            color:
              hintTone === 'success'
                ? 'var(--color-primary)'
                : hintTone === 'warn'
                  ? '#92400e'
                  : 'var(--color-on-surface-muted)',
            lineHeight: 1.4,
          }}
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

function ReviewBlock({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly hint?: string | null;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 'var(--space-3)',
        alignItems: 'baseline',
      }}
    >
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </span>
      <div>
        <p style={{ margin: 0, fontSize: 'var(--text-body)' }}>{value}</p>
        {hint ? (
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.4,
            }}
          >
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function RadioCard({
  selected,
  onSelect,
  title,
  body,
}: {
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        textAlign: 'left',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
        background: selected ? 'rgba(13,122,79,0.06)' : 'var(--color-surface)',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        display: 'grid',
        gap: 'var(--space-1)',
      }}
    >
      <span style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{title}</span>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.4,
        }}
      >
        {body}
      </span>
    </button>
  );
}

function SectionHeader({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <header>
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
          maxWidth: 580,
        }}
      >
        {body}
      </p>
    </header>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
    background: active ? 'var(--color-primary)' : 'transparent',
    color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { readonly label: string; readonly to: string };
}) {
  return (
    <main
      className="min-h-dvh"
      style={{
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-6)',
        background: 'var(--color-surface)',
        color: 'var(--color-on-surface)',
      }}
    >
      <div style={{ maxWidth: 460, textAlign: 'center', display: 'grid', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>{title}</h1>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link to={cta.to} style={primaryButtonStyle}>
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── styles ─────────────────────────────────────────────────────────────

const metaEyebrow: React.CSSProperties = {
  margin: 0,
  fontSize: '0.6875rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};
